import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
} from "react-leaflet";

import L from "leaflet";

import {
  X,
  AlertCircle,
  MapPin,
  Gauge,
  RefreshCw,
} from "lucide-react";

import {
  fetchLiveVehicleByNumber,
  fmtTimestamp,
  statusLabel,
  statusTone,
} from "../../services/fleetx_service";

import "leaflet/dist/leaflet.css";
import "./VehicleLiveMap.css";

const POLL_MS = 5000;
const MOVE_DURATION = 4500;


/* =========================================================
   VEHICLE ICON
========================================================= */

function createVehicleIcon(heading = 0) {
  const safeHeading =
    typeof heading === "number" &&
    Number.isFinite(heading)
      ? heading
      : 0;

  const rotation =
    (safeHeading + 180) % 360;

  return L.divIcon({
    html: `
      <div
        class="vlm-car-marker"
        style="--vlm-heading: ${rotation}deg;"
      >
        <img
          src="/assets/carmap.png"
          alt="Vehicle"
          class="vlm-car-image"
          draggable="false"
        />
      </div>
    `,

    className: "vlm-marker-icon",

    /*
     * Exact GPS coordinate is the
     * CENTER of the icon.
     */
    iconSize: [52, 52],
    iconAnchor: [26, 26],
  });
}


/* =========================================================
   MAP CONTROLLER
========================================================= */

function MapController({ onReady }) {
  const map = useMap();

  useEffect(() => {
    onReady(map);

    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [map, onReady]);

  return null;
}


/* =========================================================
   SMOOTH LIVE VEHICLE
========================================================= */

function LiveVehicleMarker({
  latitude,
  longitude,
  heading,
}) {
  const markerRef = useRef(null);

  const animationRef =
    useRef(null);

  const currentHeadingRef =
    useRef(
      typeof heading === "number"
        ? heading
        : 0
    );

  const firstPositionRef =
    useRef([
      latitude,
      longitude,
    ]);


  /*
   * Stop current animation.
   */
  const stopAnimation =
    useCallback(() => {
      if (
        animationRef.current !== null
      ) {
        cancelAnimationFrame(
          animationRef.current
        );

        animationRef.current = null;
      }
    }, []);


  /*
   * Find shortest rotation.
   */
  const angleDifference = (
    from,
    to
  ) => {
    let difference =
      to - from;

    while (difference > 180) {
      difference -= 360;
    }

    while (difference < -180) {
      difference += 360;
    }

    return difference;
  };


  /*
   * Change only the visual car rotation.
   */
  const applyHeading =
    useCallback((value) => {
      const marker =
        markerRef.current;

      if (!marker) return;

      const element =
        marker.getElement();

      if (!element) return;

      const car =
        element.querySelector(
          ".vlm-car-marker"
        );

      if (!car) return;

      const rotation =
        (value + 180) % 360;

      car.style.setProperty(
        "--vlm-heading",
        `${rotation}deg`
      );
    }, []);


  /*
   * IMPORTANT:
   *
   * Marker position prop is NOT updated
   * with every API update.
   *
   * We use the marker's REAL current
   * Leaflet position as the animation start.
   */
  useEffect(() => {
    const marker =
      markerRef.current;

    if (!marker) return;

    if (
      typeof latitude !== "number" ||
      typeof longitude !== "number"
    ) {
      return;
    }


    const targetLat =
      latitude;

    const targetLng =
      longitude;


    const targetHeading =
      typeof heading === "number" &&
      Number.isFinite(heading)
        ? heading
        : currentHeadingRef.current;


    /*
     * Read the marker's ACTUAL position.
     *
     * This is the critical fix.
     */
    const current =
      marker.getLatLng();


    const startLat =
      current.lat;

    const startLng =
      current.lng;


    const startHeading =
      currentHeadingRef.current;


    /*
     * First GPS position.
     */
    if (
      Math.abs(
        startLat - targetLat
      ) < 0.0000001 &&
      Math.abs(
        startLng - targetLng
      ) < 0.0000001
    ) {
      marker.setLatLng([
        targetLat,
        targetLng,
      ]);

      currentHeadingRef.current =
        targetHeading;

      applyHeading(
        targetHeading
      );

      return;
    }


    stopAnimation();


    const startTime =
      performance.now();


    const animate = (now) => {
      const elapsed =
        now - startTime;


      let progress =
        elapsed /
        MOVE_DURATION;


      if (progress >= 1) {
        progress = 1;
      }


      /*
       * Smooth ease-in/ease-out.
       */
      const eased =
        progress < 0.5
          ? 2 *
            progress *
            progress
          : 1 -
            Math.pow(
              -2 * progress + 2,
              2
            ) /
              2;


      /*
       * ACTUAL LATITUDE MOVEMENT
       */
      const nextLat =
        startLat +
        (targetLat - startLat) *
          eased;


      /*
       * ACTUAL LONGITUDE MOVEMENT
       */
      const nextLng =
        startLng +
        (targetLng - startLng) *
          eased;


      /*
       * Move actual Leaflet marker.
       */
      marker.setLatLng([
        nextLat,
        nextLng,
      ]);


      /*
       * Smooth heading.
       */
      const difference =
        angleDifference(
          startHeading,
          targetHeading
        );


      const nextHeading =
        startHeading +
        difference *
          eased;


      currentHeadingRef.current =
        nextHeading;

      applyHeading(
        nextHeading
      );


      if (progress < 1) {
        animationRef.current =
          requestAnimationFrame(
            animate
          );
      } else {
        /*
         * Force exact final GPS coordinate.
         */
        marker.setLatLng([
          targetLat,
          targetLng,
        ]);

        currentHeadingRef.current =
          targetHeading;

        applyHeading(
          targetHeading
        );

        animationRef.current =
          null;
      }
    };


    animationRef.current =
      requestAnimationFrame(
        animate
      );


    return () => {
      stopAnimation();
    };
  }, [
    latitude,
    longitude,
    heading,
    stopAnimation,
    applyHeading,
  ]);


  /*
   * Cleanup.
   */
  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, [stopAnimation]);


  /*
   * VERY IMPORTANT:
   *
   * The Marker gets its initial position
   * only from firstPositionRef.
   *
   * It does NOT receive the changing
   * latitude/longitude as a React position
   * prop.
   */
  return (
    <Marker
      ref={markerRef}
      position={firstPositionRef.current}
      icon={createVehicleIcon(
        heading
      )}
      zIndexOffset={1000}
    />
  );
}


/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function VehicleLiveMap({
  vehicleNumber,
  apiBaseUrl,
  getToken,
  onClose,
}) {
  const mapRef =
    useRef(null);

  const pollRef =
    useRef(null);

  const hasCenteredRef =
    useRef(false);


  const [vehicle, setVehicle] =
    useState(null);

  const [error, setError] =
    useState(null);

  const [
    initialLoading,
    setInitialLoading,
  ] = useState(true);


  /* =====================================================
     MAP READY
  ===================================================== */

  const handleMapReady =
    useCallback((map) => {
      mapRef.current = map;

      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }, []);


  /* =====================================================
     FETCH LIVE VEHICLE
  ===================================================== */

  const poll = useCallback(
    async () => {
      try {
        const data =
          await fetchLiveVehicleByNumber(
            apiBaseUrl,
            getToken,
            vehicleNumber
          );


        setVehicle(data);

        setError(null);


        /*
         * CENTER MAP ONLY ONCE.
         *
         * After that, user can zoom,
         * drag and inspect the map freely.
         */
        if (
          mapRef.current &&
          typeof data.latitude ===
            "number" &&
          typeof data.longitude ===
            "number"
        ) {
          if (
            !hasCenteredRef.current
          ) {
            mapRef.current.setView(
              [
                data.latitude,
                data.longitude,
              ],
              17,
              {
                animate: false,
              }
            );

            hasCenteredRef.current =
              true;
          }
        }
      } catch (e) {
        console.error(
          "Failed to fetch live vehicle:",
          e
        );

        setError(
          e?.message ||
            "Unable to update vehicle location"
        );
      } finally {
        setInitialLoading(false);
      }
    },
    [
      apiBaseUrl,
      getToken,
      vehicleNumber,
    ]
  );


  /* =====================================================
     POLLING
  ===================================================== */

  useEffect(() => {
    hasCenteredRef.current =
      false;

    poll();


    pollRef.current =
      setInterval(
        poll,
        POLL_MS
      );


    return () => {
      if (pollRef.current) {
        clearInterval(
          pollRef.current
        );

        pollRef.current = null;
      }
    };
  }, [poll]);


  const hasPosition =
    vehicle &&
    typeof vehicle.latitude ===
      "number" &&
    typeof vehicle.longitude ===
      "number";


  return (
    <div
      className="vlm-overlay"
      onClick={onClose}
    >

      <div
        className="vlm-card"
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* =========================================
            HEADER
        ========================================== */}

        <div className="vlm-header">

          <div className="vlm-header-info">

            <h3 className="vlm-title">
              {vehicleNumber}
            </h3>

            {vehicle?.vehicleName && (
              <p className="vlm-subtitle">
                {vehicle.vehicleName}
              </p>
            )}

          </div>


          <div className="vlm-header-right">

            <span className="vlm-live-badge">
              <span className="vlm-live-dot" />
              Live
            </span>


            <button
              type="button"
              className="vlm-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={19} />
            </button>

          </div>

        </div>


        {/* =========================================
            STATUS
        ========================================== */}

        {vehicle && (
          <div className="vlm-status-row">

            <span
              className={`vlm-pill vlm-pill--${statusTone(
                vehicle.currentStatus
              )}`}
            >
              <span className="vlm-pill-dot" />

              {statusLabel(
                vehicle.currentStatus
              )}
            </span>


            <span className="vlm-meta">

              <Gauge size={13} />

              {typeof vehicle.speed ===
              "number"
                ? `${Math.round(
                    vehicle.speed
                  )} km/h`
                : "—"}

            </span>


            <span className="vlm-meta">
              Updated{" "}
              {fmtTimestamp(
                vehicle.lastUpdatedAt
              )}
            </span>

          </div>
        )}


        {/* =========================================
            ERROR
        ========================================== */}

        {error && (
          <div className="vlm-error-banner">

            <AlertCircle size={14} />

            <span>
              {error} — showing last known
              position, retrying…
            </span>

          </div>
        )}


        {/* =========================================
            MAP
        ========================================== */}

        {initialLoading ? (
          <div className="vlm-map-fallback">

            <RefreshCw
              size={16}
              className="vlm-spin"
            />

            <span>
              Locating vehicle…
            </span>

          </div>
        ) : !hasPosition ? (
          <div className="vlm-map-fallback">

            <MapPin size={16} />

            <span>
              No location data for this
              vehicle yet.
            </span>

          </div>
        ) : (
          <div className="vlm-map-canvas">

            <MapContainer
              className="vlm-gmap"

              center={[
                vehicle.latitude,
                vehicle.longitude,
              ]}

              zoom={17}

              minZoom={5}
              maxZoom={19}

              scrollWheelZoom={true}
              doubleClickZoom={true}
              dragging={true}
              touchZoom={true}

              zoomControl={true}
              attributionControl={true}
            >

              {/* ===================================
                  NO API KEY REQUIRED
              ==================================== */}

              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors'

                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"

                maxZoom={19}
                maxNativeZoom={19}

                updateWhenIdle={true}
                updateWhenZooming={false}
                keepBuffer={3}
              />


              <MapController
                onReady={
                  handleMapReady
                }
              />


              <LiveVehicleMarker
                latitude={
                  vehicle.latitude
                }
                longitude={
                  vehicle.longitude
                }
                heading={
                  vehicle.heading
                }
              />

            </MapContainer>

          </div>
        )}


        {/* =========================================
            ADDRESS
        ========================================== */}

        {vehicle?.address && (
          <div className="vlm-address">

            <MapPin size={13} />

            <span>
              {vehicle.address}
            </span>

          </div>
        )}


        {/* =========================================
            FOOTER
        ========================================== */}

        <div className="vlm-poll-note">
          Live location refreshes every{" "}
          {POLL_MS / 1000}s
        </div>

      </div>
    </div>
  );
}